import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { pdfjs } from "react-pdf"
import { PDF_OPTIONS } from "../lib/pdfWorker"
import { AppShell } from "../components/AppShell"
import { useApi } from "../api/hooks"
import { createTemplate, type CreateTemplateBody } from "../api/templates"
import type { CellMeta, CellsBundle } from "../api/book"
import { detectFields } from "../lib/detectFields"
import { useT } from "../lib/i18n"

export function TemplateUploadPage() {
  const api = useApi()
  const nav = useNavigate()
  const { t } = useT()
  const [templateId, setTemplateId] = useState("")
  const [name, setName] = useState("")
  const [version, setVersion] = useState("1.0")
  const [lang, setLang] = useState<"en" | "it">("it")
  const [isPublic, setIsPublic] = useState(true)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [cellsText, setCellsText] = useState("")
  const [sectionsText, setSectionsText] = useState("[]")
  const [mappingText, setMappingText] = useState(`{ "regex": "d(\\\\d{2})(\\\\d{3})", "groups": ["day", "item"] }`)
  const [busy, setBusy] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  async function fileToBase64(f: File): Promise<string> {
    const bytes = new Uint8Array(await f.arrayBuffer())
    let bin = ""
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    return btoa(bin)
  }

  // Analyse the PDF client-side and PRE-FILL the cells JSON (still editable).
  async function analyze() {
    if (!pdfFile) { setErr("Scegli prima un PDF"); return }
    setErr(null); setAnalyzing(true)
    try {
      const data = new Uint8Array(await pdfFile.arrayBuffer())
      const pdf = await pdfjs.getDocument({ data, ...PDF_OPTIONS }).promise
      const total = pdf.numPages
      const vp = (await pdf.getPage(1)).getViewport({ scale: 1 })
      const pages: Record<string, CellMeta[]> = {}
      let totalCells = 0
      for (let p = 1; p <= total; p++) {
        setStatus(`Analizzo pagina ${p}/${total}…`)
        const found = await detectFields(pdf, p)
        pages[String(p)] = found.map((c, i) => ({
          code: `p${p}f${i + 1}`, day: 0, item: 0,
          x: c.x, y: c.y, w: c.w, h: c.h, type: "decimal", decimals: 2,
          ...(c.label ? { label: c.label } : {}),
          ...(c.section ? { section: c.section } : {}),
        }))
        totalCells += pages[String(p)].length
      }
      const bundle: CellsBundle = { version: "auto-1", total_pages: total, total_cells: totalCells, page_size_pt: [Math.round(vp.width * 100) / 100, Math.round(vp.height * 100) / 100], pages }
      setCellsText(JSON.stringify(bundle, null, 2))
      setStatus(`Rilevati ${totalCells} campi su ${total} pagine. Controlla/modifica il JSON qui sotto, poi crea.`)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setAnalyzing(false)
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    if (!/^[a-z0-9-]{3,60}$/.test(templateId)) { setErr("Template ID: ^[a-z0-9-]{3,60}$"); return }
    if (!name.trim()) { setErr("Inserisci un nome"); return }
    if (!pdfFile) { setErr("Scegli un PDF"); return }
    if (pdfFile.size > 12 * 1024 * 1024) { setErr("PDF troppo grande (max 12 MB)"); return }
    let cells, sections, mapping
    try { cells = JSON.parse(cellsText) } catch { setErr("cells JSON non valido (usa «Analizza PDF» o incollalo)"); return }
    try { sections = JSON.parse(sectionsText) } catch { setErr("sections JSON non valido"); return }
    try { mapping = JSON.parse(mappingText) } catch { setErr("mapping JSON non valido"); return }
    try {
      setBusy(true)
      const pdf_base64 = await fileToBase64(pdfFile)
      const body: CreateTemplateBody = {
        template_id: templateId, name, version, lang,
        sections, mapping, public: isPublic, pdf_base64, cells_json: cells,
        total_pages: cells?.total_pages ?? 0,
      }
      await createTemplate(api, body)
      nav(`/cells/${encodeURIComponent(templateId)}/main`)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppShell>
      <h1 style={{ margin: 0, fontSize: 28 }}>{t("upload_title")}</h1>
      <p style={{ color: "var(--muted)", maxWidth: 700 }}>
        Scegli il PDF e premi <strong>Analizza PDF</strong>: rilevo caselle ed etichette e pre-compilo il JSON delle celle, che resta modificabile/incollabile. Poi crei e rifinisci nell'editor.
      </p>
      <form onSubmit={onSubmit} style={{ display: "grid", gap: 14, maxWidth: 760, marginTop: 24 }}>
        <Field label={t("upload_id")}>
          <input value={templateId} onChange={(e) => setTemplateId(e.target.value)} style={input} placeholder="my-form-2026" />
          <p style={{ margin: "4px 0 0 0", fontSize: 11, color: "var(--muted)" }}>{t("upload_id_hint")}</p>
        </Field>
        <Field label={t("upload_name")}>
          <input value={name} onChange={(e) => setName(e.target.value)} style={input} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label={t("upload_version")}>
            <input value={version} onChange={(e) => setVersion(e.target.value)} style={input} />
          </Field>
          <Field label={t("upload_lang")}>
            <select value={lang} onChange={(e) => setLang(e.target.value as "en" | "it")} style={input}>
              <option value="en">English</option>
              <option value="it">Italiano</option>
            </select>
          </Field>
        </div>
        <Field label={t("upload_pdf")}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)} style={{ ...input, flex: 1 }} />
            <button type="button" onClick={analyze} disabled={!pdfFile || analyzing} style={{ ...primary, background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)" }}>
              {analyzing ? "Analizzo…" : "🔍 Analizza PDF"}
            </button>
          </div>
          {pdfFile && <span style={{ fontSize: 12, color: "var(--muted)" }}>{pdfFile.name} · {(pdfFile.size / 1024).toFixed(1)} KB</span>}
        </Field>
        <Field label={`${t("upload_cells")} (auto da «Analizza», o incolla)`}>
          <textarea value={cellsText} onChange={(e) => setCellsText(e.target.value)} rows={10} style={{ ...input, fontFamily: "ui-monospace, monospace", fontSize: 12 }} placeholder='Premi «Analizza PDF» per generarlo, oppure incolla qui il cells.json' />
        </Field>
        <Field label={t("upload_sections")}>
          <textarea value={sectionsText} onChange={(e) => setSectionsText(e.target.value)} rows={3} style={{ ...input, fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
        </Field>
        <Field label={t("upload_mapping")}>
          <textarea value={mappingText} onChange={(e) => setMappingText(e.target.value)} rows={2} style={{ ...input, fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
        </Field>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
          {t("upload_public")}
        </label>
        {status && <p style={{ color: "var(--muted)", fontSize: 13 }}>{status}</p>}
        {err && <p style={{ color: "#ff7a85" }}>{err}</p>}
        <button type="submit" disabled={busy} style={primary}>
          {busy ? t("upload_saving") : t("upload_submit")}
        </button>
      </form>
    </AppShell>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  )
}

const input: React.CSSProperties = {
  width: "100%", padding: "8px 10px", background: "var(--surface)", color: "var(--text)",
  border: "1px solid var(--border)", borderRadius: 8, fontSize: 14, fontFamily: "var(--font)", outline: "none",
}
const primary: React.CSSProperties = {
  background: "var(--accent)", color: "var(--accent-fg)", border: "none", borderRadius: 8,
  padding: "10px 16px", fontWeight: 600, cursor: "pointer", fontSize: 14,
}
